import { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from "react"
import { AddModelObjectProvider } from "components/providers/Forms.provider"
import { Form, Input, Skeleton } from "antd"
import { H4 } from "components/ui/typography/titles/Titles"
import { OrderedModelNumbersSelect } from "components/ui/orderedModelNumbersSelect/OrderedModelNumbersSelect"
import { ModelNumberOrderInfo } from "components/ui/modelNumberOrderInfo/ModelNumberOrderInfo"
import { Collapse } from "components/ui/collapse/Collapse"
import { ButtonPlus } from "components/ui/buttons/ButtonPlus/ButtonPlus"
import { CombinationChildren } from "./layout/CombinationChildren/CombinationChildren"
import { DeleteIcon } from "components/ui/icons/delete/DeleteIcon"
import { useObjectsCategories } from "hooks/api/objectsCategories/objectsCategories.hook"
import { useCategories } from "hooks/categories.hook"
import { useCompositeCategoryStore } from "store/compositeCategory.store"
import { Select } from "components/ui/select/Select"
import { useWithoutCategoryModelNumbers } from "hooks/withoutCategoryModelNumbers.hook"
import { Tooltip } from "components/ui/tooltip/Tooltip"
import { OBJECT_LABEL_VALIDATION, OBJECT_VALIDATION_MESSAGES } from "helpers/validation/objectValidation.helper"
import type { TTopLevelCategory } from "types/drawer.type"
import type { IAddObjectMethods } from "types/configuration.type"
import style from "./CompositeObject.module.scss"

const {
	addModelObjectForm,
	block,
	label,
	description,
	endOfSection,
	title,
	combination,
	startOfSection,
	select,
	skeleton,
	collapse,
} = style

type Props = {
	objectCategoriesData: TTopLevelCategory[]
}

export const CompositeObject = forwardRef<IAddObjectMethods, Props>(({ objectCategoriesData }: Props, ref) => {
	const [form] = Form.useForm()
	const values = Form.useWatch([], form)

	const { data: compositeCategories, isPending: compositeCategoriesPending } = useObjectsCategories({
		filterAtomic: true,
	})

	const [selectedTopLevelCategory, setSelectedTopLevelCategory] = useState<string | number | undefined>()

	const { topLevelCategories, superCategories } = useCategories(objectCategoriesData, selectedTopLevelCategory)

	const {
		setFormValues,
		category,
		setModelNumbers,
		deleteVariant,
		resetForm: resetStoreForm,
	} = useCompositeCategoryStore()

	const { options: modelNumberOptions } = useWithoutCategoryModelNumbers()

	const [combinations, setCombinations] = useState([
		{
			id: "1",
			label: "Combination 1",
		},
	])
	const [nextId, setNextId] = useState(2)

	useEffect(() => {
		setFormValues({
			name: values?.name,
			label: values?.label,
			topLevelCategory: values?.topLevelCategory,
			superCategory: values?.superCategory,
		})
	}, [setFormValues, values])

	useImperativeHandle(ref, () => ({
		resetForm: () => {
			form.resetFields()
			resetStoreForm()
		},
	}))

	// Change top level category
	const onChangeTopLevelCategoryHandle = (value: string | number) => {
		setSelectedTopLevelCategory(value)
		form.resetFields(["superCategory"])
	}

	const onModelNumbersChange = useCallback(
		(selectedModelNumbers: number[]) => {
			setModelNumbers(selectedModelNumbers)
		},
		[setModelNumbers],
	)

	const addCombinationHandle = () => {
		setCombinations((prev) => [
			...prev,
			{
				id: nextId.toString(),
				label: `Combination ${nextId}`,
			},
		])
		setNextId((prev) => prev + 1)
	}

	const deleteCombinationHandle = (id: string) => {
		setCombinations((prev) => prev.filter((combination) => combination.id !== id))
		deleteVariant(id)
	}

	return (
		<AddModelObjectProvider>
			<Form form={form} size="large">
				<div className={addModelObjectForm}>
					<div className={block}>
						<span className={label}>Name</span>
						<Form.Item name="name" rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.NAME_REQUIRED }]}>
							<Input data-testid="name" />
						</Form.Item>
					</div>
					<div className={`${block} ${endOfSection}`}>
						<span className={label}>Label</span>
						<span className={description}>
							<span className={description}>Used in train set</span>
							<Form.Item
								name="label"
								required={true}
								rules={[
									{ required: true, message: OBJECT_VALIDATION_MESSAGES.LABEL_REQUIRED },
									OBJECT_LABEL_VALIDATION.LABEL_PREFIX,
								]}
							>
								<Input data-testid="label" />
							</Form.Item>
						</span>
					</div>
					{compositeCategoriesPending ? (
						<div className={skeleton} data-testid="compositeCategoriesSkeleton">
							<Skeleton />
						</div>
					) : (
						<div className={`${block} ${endOfSection}`}>
							<H4 className={title}>Composite categories</H4>
							<div className={combination}>
								{combinations.map((combinationItem) => (
									<Collapse
										key={combinationItem.id}
										className={collapse}
										items={[
											{
												key: combinationItem.id,
												label: combinationItem.label,
												"data-testid": "collapse-item",
												children: (
													<CombinationChildren categories={compositeCategories} combinationId={combinationItem.id} />
												),
												extra:
													combinations.length > 1 ? (
														<Tooltip text="Delete the entire combination" placement="bottom">
															<button
																onClick={() => deleteCombinationHandle(combinationItem.id)}
																type="button"
																data-testid={`deleteCombination-${combinationItem.id}`}
															>
																<DeleteIcon />
															</button>
														</Tooltip>
													) : undefined,
											},
										]}
									/>
								))}
							</div>
							<ButtonPlus type="button" onClick={addCombinationHandle} active={combinations.length < 4}>
								Add combination
							</ButtonPlus>
						</div>
					)}
					<div className={block}>
						<H4 className={title}>Categories</H4>
						<span className={label}>Top level category</span>
						<Form.Item
							name="topLevelCategory"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.TOP_LEVEL_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								allowClear={false}
								variant="outlined"
								options={topLevelCategories}
								onChange={onChangeTopLevelCategoryHandle}
								className={select}
							/>
						</Form.Item>
					</div>
					<div className={block}>
						<span className={label}>Super category</span>
						<Form.Item
							name="superCategory"
							required={true}
							rules={[{ required: true, message: OBJECT_VALIDATION_MESSAGES.SUPER_CATEGORY_REQUIRED }]}
						>
							<Select
								placeholder="Select category"
								variant="outlined"
								allowClear={false}
								className={select}
								options={superCategories}
							/>
						</Form.Item>
					</div>
					<div className={`${block} ${startOfSection}`}>
						<ModelNumberOrderInfo />
						<OrderedModelNumbersSelect
							value={category.modelNumbers}
							onChange={onModelNumbersChange}
							options={modelNumberOptions}
							data-testid="model-numbers-select"
						/>
					</div>
				</div>
			</Form>
		</AddModelObjectProvider>
	)
})
